import { formatTranslation } from 'intl';
import { AnalyticsTranslationKeys } from 'intl/keys';
import type { IntlShape } from 'react-intl';
import {
  ChartAxisSeries,
  ChartAxisSeriesItem,
  DateRangeIntervalWithoutWeek,
  InspectionEvolutionFilters,
} from 'shared/types/analytics';
import {
  INSPECTION_EVOLUTION_COMPLETED_SERIES_NAME,
  INSPECTION_EVOLUTION_SCORE_SERIES_NAME,
} from 'shared/constants/ANALYTICS';
import { isoToProjectTimezonedDate } from 'shared/utils/date/dates';
import type { ChartRawData } from '../catalog/types';
import { mapLineSeries } from '../model/mapSeriesToRawData';

export function localizeSeriesName(
  seriesItem: ChartAxisSeriesItem,
  completedLabel: string,
  scoreLabel: string
): ChartAxisSeriesItem {
  if (seriesItem.name === INSPECTION_EVOLUTION_COMPLETED_SERIES_NAME) {
    return {
      ...seriesItem,
      name: completedLabel,
    };
  }

  if (seriesItem.name === INSPECTION_EVOLUTION_SCORE_SERIES_NAME) {
    return {
      ...seriesItem,
      name: scoreLabel,
    };
  }

  return seriesItem;
}

const categoriesFormatByInterval = {
  [DateRangeIntervalWithoutWeek.day]: {
    day: '2-digit' as const,
    month: 'short' as const,
    year: 'numeric' as const,
  },
  [DateRangeIntervalWithoutWeek.month]: {
    month: 'short' as const,
    year: 'numeric' as const,
  },
  [DateRangeIntervalWithoutWeek.year]: {
    year: 'numeric' as const,
  },
};

function createXAxisCategories(
  res: string[] | undefined,
  interval: DateRangeIntervalWithoutWeek,
  intlContext: IntlShape,
  timezone: string
): string[] {
  if (!res) return [];
  return res.map((r) =>
    isoToProjectTimezonedDate(r, timezone).toLocaleString(
      categoriesFormatByInterval[interval],
      {
        locale: intlContext.locale,
      }
    )
  );
}

export function buildInspectionEvolutionRawData(
  series: ChartAxisSeries,
  filters: InspectionEvolutionFilters,
  intl: IntlShape,
  timezone: string
): ChartRawData {
  const [completedRawSeries, scoreRawSeries] = series;
  const [dateIntervalFilter] = filters.dataScope.filters;
  const completedLabel = formatTranslation(
    intl,
    AnalyticsTranslationKeys.inspectionEvolutionCompleted
  );
  const scoreLabel = formatTranslation(
    intl,
    AnalyticsTranslationKeys.inspectionResultChart
  );

  const completedSeries = localizeSeriesName(
    completedRawSeries ?? { data: [] },
    completedLabel,
    scoreLabel
  );
  const scoreSeries = localizeSeriesName(
    scoreRawSeries ?? { data: [] },
    completedLabel,
    scoreLabel
  );

  const categories = createXAxisCategories(
    completedSeries.labels,
    dateIntervalFilter.value,
    intl,
    timezone
  );

  return mapLineSeries([completedSeries, scoreSeries], categories);
}
